// BarcodeScanner.jsx
// Shared barcode scanner component using BarcodeDetector API (via ponyfill)
// Manages camera stream + detection loop. Consumers embed in their own UI.

import React, { Component } from 'react';

class BarcodeScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cameraReady: false,
      error: null
    };

    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
    this.detector = null;
    this.stream = null;
    this.rafId = null;
    this.lastDetectTime = 0;
    this.lastDetectedValue = null;
    this.lastDetectedTime = 0;
  }

  async componentDidMount() {
    try {
      const { BarcodeDetector } = await import(/* webpackChunkName: "barcode-detector" */ 'barcode-detector/ponyfill');
      const formats = this.props.formats || ['code_128', 'code_39'];
      this.detector = new BarcodeDetector({ formats });
      await this.startCamera();
    } catch (err) {
      console.error('BarcodeScanner init error:', err);
      const msg = 'Unable to initialize barcode scanner.';
      this.setState({ error: msg });
      if (this.props.onError) this.props.onError(msg);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.active !== this.props.active) {
      if (this.props.active && this.detector && this.stream && !this.rafId) {
        this.startDetectionLoop();
      }
    }
  }

  componentWillUnmount() {
    this.stopDetectionLoop();
    this.stopCamera();
  }

  startCamera = async () => {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = this.stream;
        // Wait for video to be ready before starting detection
        this.videoRef.current.onloadedmetadata = () => {
          this.setState({ cameraReady: true });
          if (this.props.active !== false) {
            this.startDetectionLoop();
          }
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      const msg = 'Camera access denied. Please enable camera permissions.';
      this.setState({ error: msg });
      if (this.props.onError) this.props.onError(msg);
    }
  };

  stopCamera = () => {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  };

  startDetectionLoop = () => {
    if (this.rafId) return;

    const loop = async () => {
      this.rafId = requestAnimationFrame(async () => {
        if (!this.detector || !this.videoRef.current || this.props.active === false) {
          this.rafId = null;
          return;
        }

        const now = Date.now();
        const throttleMs = this.props.throttleMs || 150;

        if (now - this.lastDetectTime >= throttleMs) {
          this.lastDetectTime = now;
          try {
            const source = this.getCropSource();
            const barcodes = await this.detector.detect(source);

            if (barcodes.length > 0) {
              const { rawValue, format } = barcodes[0];

              // Deduplication: ignore same value within 2 seconds
              if (rawValue !== this.lastDetectedValue || now - this.lastDetectedTime > 2000) {
                this.lastDetectedValue = rawValue;
                this.lastDetectedTime = now;
                if (this.props.onDetected) {
                  this.props.onDetected(rawValue, format);
                }
              }
            }
          } catch (err) {
            // detect() can throw if video not ready — ignore
          }
        }

        loop();
      });
    };

    loop();
  };

  stopDetectionLoop = () => {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  getCropSource = () => {
    const video = this.videoRef.current;
    const { scanRegion } = this.props;

    if (!scanRegion || !this.canvasRef.current) {
      return video;
    }

    const canvas = this.canvasRef.current;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const cropW = Math.round(vw * (scanRegion.widthPercent || 0.75));
    const cropH = Math.round(vh * (scanRegion.heightPercent || 0.25));
    const sx = Math.round((vw - cropW) / 2);
    const sy = Math.round((vh - cropH) / 2);

    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, cropW, cropH);

    return canvas;
  };

  render() {
    const { cameraReady, error } = this.state;
    const { scanRegion } = this.props;

    if (error) {
      return (
        <div style={styles.errorContainer}>
          <i className="fa-sharp fa-regular fa-exclamation-triangle fa-2x mb-2 text-danger"></i>
          <p className="text-danger">{error}</p>
        </div>
      );
    }

    return (
      <div style={styles.wrapper}>
        <video
          ref={this.videoRef}
          autoPlay
          playsInline
          muted
          style={styles.video}
        />
        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        {/* Loading spinner while camera initializes */}
        {!cameraReady && (
          <div style={styles.loadingOverlay}>
            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x text-white"></i>
            <p style={styles.loadingText}>Starting camera...</p>
          </div>
        )}

        {/* Scan region overlay */}
        {cameraReady && scanRegion && (
          <div style={styles.regionOverlay}>
            <div style={{
              ...styles.regionBox,
              width: `${(scanRegion.widthPercent || 0.75) * 100}%`,
              height: `${(scanRegion.heightPercent || 0.25) * 100}%`
            }}>
              <div style={styles.regionCornerTL} />
              <div style={styles.regionCornerTR} />
              <div style={styles.regionCornerBL} />
              <div style={styles.regionCornerBR} />
            </div>
          </div>
        )}
      </div>
    );
  }
}

const CORNER_SIZE = 20;
const CORNER_WEIGHT = 3;
const CORNER_COLOR = 'var(--color-success)';

const cornerBase = {
  position: 'absolute',
  width: CORNER_SIZE,
  height: CORNER_SIZE,
  borderColor: CORNER_COLOR,
  borderStyle: 'solid',
  borderWidth: 0
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-inverse)'
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)'
  },
  loadingText: {
    color: 'var(--color-text-inverse)',
    marginTop: 12,
    fontSize: 14
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
    textAlign: 'center'
  },
  regionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    // Semi-transparent overlay around scan region
    background: 'radial-gradient(ellipse, transparent 30%, rgba(0,0,0,0.5) 70%)'
  },
  regionBox: {
    position: 'relative',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 4
  },
  regionCornerTL: {
    ...cornerBase,
    top: -1,
    left: -1,
    borderTopWidth: CORNER_WEIGHT,
    borderLeftWidth: CORNER_WEIGHT,
    borderTopLeftRadius: 4
  },
  regionCornerTR: {
    ...cornerBase,
    top: -1,
    right: -1,
    borderTopWidth: CORNER_WEIGHT,
    borderRightWidth: CORNER_WEIGHT,
    borderTopRightRadius: 4
  },
  regionCornerBL: {
    ...cornerBase,
    bottom: -1,
    left: -1,
    borderBottomWidth: CORNER_WEIGHT,
    borderLeftWidth: CORNER_WEIGHT,
    borderBottomLeftRadius: 4
  },
  regionCornerBR: {
    ...cornerBase,
    bottom: -1,
    right: -1,
    borderBottomWidth: CORNER_WEIGHT,
    borderRightWidth: CORNER_WEIGHT,
    borderBottomRightRadius: 4
  }
};

export default BarcodeScanner;
